import { log, warn, error } from '@/utils/debugLogger';

let currentProcessId = null;
let currentController = null;

/**
 * Safely adds 3D status options to kanban columns with retry mechanism
 * @param {Object} config Configuration object
 * @param {number} [config.maxRetries=5] Maximum number of retry attempts
 * @param {number} [config.retryDelay=1000] Delay between retries in milliseconds
 * @param {Object} config.taskType Task type object containing statuses
 * @param {Array} config.entries Array of entries with status and forge tags
 * @returns {Promise<boolean>} Promise that resolves to success status
 */
export function addStatus3dOption({ maxRetries = 5, retryDelay = 1000, taskType, entries } = {}) {
  let isProcessing = false;
  let retryCount = 0;
  const processId = Date.now().toString();

  // Cancel any existing process
  if (currentController) {
    currentController.abort();
  }

  // Create new abort controller
  currentController = new AbortController();
  currentProcessId = processId;

  function waitForElements() {
    return new Promise((resolve) => {
      function check() {
        const columns = document.querySelectorAll('.wx-column.svelte-ms9wpd.svelte-ms9wpd');
        if (columns.length > 0) {
          resolve(true);
        } else if (retryCount < maxRetries) {
          retryCount++;
          setTimeout(check, retryDelay);
        } else {
          resolve(false);
        }
      }
      check();
    });
  }

  function getValidStatusIds(entries) {
    return new Set(
      entries
        .filter(e => e.forgeTagsIds && e.forgeTagsIds.length)
        .map(e => e.statusId)
    );
  }

  function clearExisting3DButtons() {
    const buttons = document.querySelectorAll(
      '.wx-column.svelte-ms9wpd.svelte-ms9wpd .wx-show-3d.svelte-vhwr63'
    );
    buttons.forEach(button => button.remove());
  }

  function create3DButton(statusId) {
    const newDiv = document.createElement('div');
    newDiv.className = 'wx-show-3d svelte-vhwr63 mr-2';
    newDiv.setAttribute('data-ignore-selection', 'true');
    newDiv.setAttribute('onclick', `showStatusIn3D('${statusId}')`);

    const icon = document.createElement('i');
    icon.className = 'wx-icon svelte-p36u8h mdi mdi-cube-outline';
    icon.style.fontSize = '24px';
    newDiv.appendChild(icon);

    return newDiv;
  }

  return new Promise((resolve, reject) => {

    const signal = currentController.signal;

    signal.addEventListener('abort', () => {
      resolve(true);
    });

    try {
      if (isProcessing) {
        warn('Status 3D options update already in progress');
        resolve(false);
        return;
      }

      isProcessing = true;

      waitForElements()
        .then(elementsReady => {
          if (!elementsReady) {
            throw new Error('Required DOM elements not found after maximum retries');
          }

          const validStatusIds = getValidStatusIds(entries);
          clearExisting3DButtons();

          const columns = document.querySelectorAll('.wx-column.svelte-ms9wpd.svelte-ms9wpd');

          // Apply status colors
          for (let i = 0; i < taskType.statuses.length; i++) {
            if (columns[i]) {
              columns[i].style.borderTop = `5px solid ${taskType.statuses[i].color}`;
            }
          }

          // Add 3D buttons
          columns.forEach(column => {
            const statusId = column.getAttribute('data-column-header');
            if (!statusId || !validStatusIds.has(statusId)) {
              return;
            }

            const menu = column.querySelector('.wx-menu.svelte-ms9wpd.svelte-ms9wpd');
            if (!menu) {
              warn(`Menu element not found for status ${statusId}`);
              return;
            }

            const button = create3DButton(statusId);
            menu.parentNode?.insertBefore(button, menu);
          });

          resolve(true);
        })
        .catch(err => {
          error('Error adding 3D status options:', err);
          resolve(false);
        })
        .finally(() => {
          if (currentProcessId === processId) {
            currentController = null;
            currentProcessId = null;
          }
          isProcessing = false;
        });
    } catch (err) {
      reject(err);
    }
  });
}

/**
 * Helper function to safely retry the addStatus3dOption operation
 * @param {Object} config Configuration options
 * @param {number} [maxAttempts=3] Maximum number of retry attempts
 * @returns {Promise<boolean>} Promise that resolves when operation succeeds or max attempts reached
 */
export function retryAddStatus3dOption(config, maxAttempts = 3) {
  let attempts = 0;

  function attempt() {
    return new Promise((resolve) => {
      addStatus3dOption(config)
        .then(success => {
          if (success) {
            resolve(true);
          } else {
            attempts++;
            if (attempts < maxAttempts) {
              setTimeout(() => {
                attempt().then(resolve);
              }, config.retryDelay || 1000);
            } else {
              resolve(false);
            }
          }
        })
        .catch(() => resolve(false));
    });
  }

  return attempt();
}